<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">RFM 分析 Dashboard</h1>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchData">重試</button>
    </div>

    <div v-else class="dashboard-content">
      <!-- 統計卡片 -->
      <div class="stats-cards">
        <div class="stat-card">
          <div class="stat-label">總會員數</div>
          <div class="stat-value">{{ dashboard.totalMembers || 0 }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">平均 RFM 分數</div>
          <div class="stat-value">{{ dashboard.averageRfmScore || '-' }}</div>
        </div>
      </div>

      <!-- 分群分布圖 -->
      <div class="chart-section">
        <div class="chart-card">
          <PieChart
            v-if="segmentData.length > 0"
            title="RFM 分群分布"
            :data="segmentData"
            nameKey="segment"
            valueKey="count"
            height="400px"
          />
        </div>
      </div>

      <!-- Top 分群列表 -->
      <div class="table-section">
        <h3>主要分群</h3>
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>分群</th>
                <th>會員數</th>
                <th>占比</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!dashboard.topSegments || dashboard.topSegments.length === 0">
                <td colspan="3" class="empty-state">沒有資料</td>
              </tr>
              <tr v-for="segment in dashboard.topSegments" :key="segment.segment">
                <td>{{ getSegmentDisplayName(segment.segment) }}</td>
                <td>{{ segment.count }}</td>
                <td>{{ segment.percentage }}%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { analyticsService } from '@/api/modules/analytics.js';
import { showError } from '@/utils/swal.js';
import PieChart from '@/components/charts/PieChart.vue';

export default {
  name: 'RfmDashboard',
  components: {
    PieChart
  },
  setup() {
    const loading = ref(false);
    const error = ref(null);
    const dashboard = ref({});

    const segmentData = computed(() => {
      if (!dashboard.value.segmentDistribution) return [];
      return Object.entries(dashboard.value.segmentDistribution).map(([segment, count]) => ({
        segment: getSegmentDisplayName(segment),
        count
      }));
    });

    const getSegmentDisplayName = (segment) => {
      const segmentMap = {
        'champions': '冠軍客戶',
        'loyal_customers': '忠誠客戶',
        'potential_loyalists': '潛力忠誠客戶',
        'new_customers': '新客戶',
        'promising': '有潛力客戶',
        'needs_attention': '需要關注',
        'about_to_sleep': '即將沉睡',
        'cannot_lose_them': '不能失去',
        'at_risk': '有風險',
        'hibernating': '休眠客戶',
        'lost': '流失客戶'
      };
      return segmentMap[segment] || segment;
    };

    const fetchData = async () => {
      try {
        loading.value = true;
        error.value = null;
        const response = await analyticsService.getRfmDashboard();
        if (response.success) {
          dashboard.value = response.data;
        } else {
          error.value = response.message || '取得資料失敗';
        }
      } catch (err) {
        error.value = err.response?.data?.message || err.message || '取得資料失敗';
        showError('錯誤', error.value);
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      fetchData();
    });

    return {
      loading,
      error,
      dashboard,
      segmentData,
      getSegmentDisplayName,
      fetchData
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.dashboard-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stat-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 32px;
  font-weight: 600;
  color: #333;
}

.chart-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.chart-card {
  width: 100%;
}

.table-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.table-section h3 {
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
}
</style>
